/**
 * <hy-product-lightbox>: full-screen image view of the product page
 * (snippets/hy-product-lightbox.liquid, AP-272). Catches clicks on the
 * gallery's .hy-product-gallery__zoom links and opens a native <dialog> at
 * the clicked image. Without this module the links stay real links to the
 * large image file.
 *
 * Navigation: arrows, arrow keys, thumbnails, and swiping — the track is a
 * native scroll-snap container, so touch swiping needs no script. Zoom:
 * click (mouse) or double tap (touch), never beyond the source resolution
 * (data-source-width); the zoomed area follows the mouse, or the finger when
 * dragging. On close, hy:lightbox:closed tells the gallery which image was
 * shown last.
 */
const MAX_ZOOM = 2.5;
const MIN_USEFUL_ZOOM = 1.1;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_DISTANCE = 30;
const HINT_MS = 1400;
const SCROLL_FALLBACK_MS = 800;

const clampPercent = (value) => Math.min(100, Math.max(0, value));

class HyProductLightbox extends HTMLElement {
  #controller = null;
  #resizeObserver = null;
  #dialog = null;
  #track = null;
  #slides = [];
  #index = 0;
  #frame = 0;
  #hintTimer = 0;
  #trigger = null;
  #zoom = null;
  #drag = null;
  #lastTap = null;
  #lastPointerType = 'mouse';
  #scrollingTo = null;
  #scrollTimer = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#dialog = this.querySelector('.hy-product-lightbox__dialog');
    this.#track = this.querySelector('.hy-product-lightbox__track');
    this.#slides = Array.from(this.querySelectorAll('.hy-product-lightbox__slide'));
    if (!this.#dialog || !this.#track || this.#slides.length === 0) return;

    this.#root().addEventListener('click', this.#handleTriggerClick, { signal });
    this.#dialog.addEventListener('click', this.#handleDialogClick, { signal });
    this.#dialog.addEventListener('keydown', this.#handleKeydown, { signal });
    this.#dialog.addEventListener('close', this.#handleClose, { signal });
    this.#track.addEventListener('scroll', this.#handleScroll, { signal, passive: true });
    this.#track.addEventListener('scrollend', this.#endProgrammaticScroll, { signal });
    this.#track.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    this.#track.addEventListener('pointermove', this.#handlePointerMove, { signal });
    this.#track.addEventListener('pointerup', this.#handlePointerUp, { signal });
    this.#track.addEventListener('pointercancel', this.#handlePointerUp, { signal });
    this.#track.addEventListener('click', this.#handleImageClick, { signal });

    this.#resizeObserver = new ResizeObserver(() => {
      if (!this.#dialog.open) return;
      this.#unzoom();
      this.#scrollTo(this.#index, 'instant');
      this.#updateZoomButton();
    });
    this.#resizeObserver.observe(this.#track);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#hintTimer);
    clearTimeout(this.#scrollTimer);
    if (this.#dialog?.open) document.documentElement.style.removeProperty('overflow');
  }

  // The gallery around the lightbox: the multi-media <hy-product-gallery> or
  // the single-media wrapper, both carry .hy-product-gallery.
  #root() {
    return this.closest('.hy-product-gallery') || this.parentElement || this;
  }

  #handleTriggerClick = (event) => {
    const link = event.target.closest('.hy-product-gallery__zoom');
    if (!link || this.contains(link)) return;
    const index = this.#slides.findIndex((slide) => slide.dataset.mediaId === link.dataset.mediaId);
    if (index === -1) return;

    event.preventDefault();
    this.#open(index, link);
  };

  #open(index, trigger) {
    this.#trigger = trigger;
    // showModal() makes the page inert but does not stop wheel/touch
    // scrolling in every browser; documentElement is the scroller.
    document.documentElement.style.overflow = 'hidden';
    this.#dialog.showModal();
    this.#scrollTo(index, 'instant');
    this.#setIndex(index);
    this.querySelector('.hy-product-lightbox__close')?.focus();
  }

  #handleClose = () => {
    this.#unzoom();
    document.documentElement.style.removeProperty('overflow');
    const mediaId = this.#slides[this.#index]?.dataset.mediaId;
    document.dispatchEvent(new CustomEvent('hy:lightbox:closed', {
      detail: { sectionId: this.dataset.sectionId, mediaId },
    }));
    const link = this.#root().querySelector(`.hy-product-gallery__zoom[data-media-id="${mediaId}"]`);
    (link || this.#trigger)?.focus({ preventScroll: true });
  };

  #handleDialogClick = (event) => {
    const target = event.target;
    if (target.closest('.hy-product-lightbox__close')) this.#dialog.close();
    else if (target.closest('.hy-product-lightbox__arrow--prev')) this.#go(this.#index - 1);
    else if (target.closest('.hy-product-lightbox__arrow--next')) this.#go(this.#index + 1);
    else if (target.closest('.hy-product-lightbox__zoom')) this.#toggleZoom();
    else {
      const thumb = target.closest('[data-hy-lightbox-index]');
      if (thumb) this.#go(Number(thumb.dataset.hyLightboxIndex));
    }
  };

  // stopPropagation: the dialog sits inside <hy-product-gallery>, whose own
  // arrow-key handler would otherwise step the gallery behind it as well.
  #handleKeydown = (event) => {
    const actions = {
      ArrowLeft: () => this.#go(this.#index - 1),
      ArrowRight: () => this.#go(this.#index + 1),
      '+': () => this.#zoomIn(),
      '=': () => this.#zoomIn(),
      '-': () => this.#unzoom(),
    };
    const action = actions[event.key];
    if (!action) return;
    event.preventDefault();
    event.stopPropagation();
    action();
  };

  // While an arrow/thumbnail jump scrolls smoothly, the positions passed on
  // the way are not announced — only the target (set in #go) counts.
  #handleScroll = () => {
    if (this.#zoom || this.#scrollingTo !== null) return;
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const width = this.#track.clientWidth;
      if (!width) return;
      const index = Math.round(Math.abs(this.#track.scrollLeft) / width);
      if (index !== this.#index) this.#setIndex(index);
    });
  };

  #go(index) {
    const count = this.#slides.length;
    const next = (index + count) % count;
    this.#unzoom();
    const behavior = this.#reducedMotion() ? 'instant' : 'smooth';
    if (behavior === 'smooth') {
      this.#scrollingTo = next;
      clearTimeout(this.#scrollTimer);
      // Fallback for browsers without scrollend.
      this.#scrollTimer = setTimeout(this.#endProgrammaticScroll, SCROLL_FALLBACK_MS);
    }
    this.#scrollTo(next, behavior);
    this.#setIndex(next);
  }

  #endProgrammaticScroll = () => {
    clearTimeout(this.#scrollTimer);
    this.#scrollingTo = null;
  };

  #scrollTo(index, behavior) {
    const direction = getComputedStyle(this.#track).direction === 'rtl' ? -1 : 1;
    this.#track.scrollTo({ left: direction * index * this.#track.clientWidth, behavior });
  }

  #setIndex(index) {
    this.#index = index;
    const current = this.querySelector('.hy-product-lightbox__counter-current');
    if (current) current.textContent = String(index + 1);

    this.querySelectorAll('.hy-product-lightbox__thumb').forEach((thumb, i) => {
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    this.querySelectorAll('.hy-product-lightbox__dot').forEach((dot, i) => {
      dot.toggleAttribute('data-current', i === index);
    });

    const status = this.querySelector('[data-hy-lightbox-status]');
    const template = this.dataset.positionTemplate;
    if (status && template && this.#slides.length > 1) {
      status.textContent = template.replace('[position]', String(index + 1));
    }
    this.#updateZoomButton();
  }

  #currentImage() {
    return this.#slides[this.#index]?.querySelector('img') || null;
  }

  // Largest scale that stays at or below the source resolution. offsetWidth
  // ignores the transform, so this holds while zoomed as well.
  #maxScale(image) {
    const slide = image.closest('.hy-product-lightbox__slide');
    const source = Number(slide?.dataset.sourceWidth);
    if (!source || !image.offsetWidth) return 1;
    return Math.min(MAX_ZOOM, source / image.offsetWidth);
  }

  #updateZoomButton() {
    const button = this.querySelector('.hy-product-lightbox__zoom');
    const image = this.#currentImage();
    const zoomable = Boolean(image) && this.#maxScale(image) > MIN_USEFUL_ZOOM;
    this.#slides.forEach((slide, i) => {
      slide.querySelector('img')?.classList.toggle('hy-product-lightbox__image--zoomable', i === this.#index && zoomable);
    });
    if (!button) return;
    // aria-disabled, not disabled: a disabled button would drop keyboard
    // focus to <body>, outside the dialog's key handler.
    button.setAttribute('aria-disabled', String(!zoomable && !this.#zoom));
    button.setAttribute('aria-pressed', String(Boolean(this.#zoom)));
  }

  #toggleZoom() {
    if (this.#zoom) this.#unzoom();
    else this.#zoomIn();
  }

  #zoomIn(originX = 50, originY = 50) {
    const image = this.#currentImage();
    if (!image || this.#zoom) return;
    const scale = this.#maxScale(image);
    if (scale <= MIN_USEFUL_ZOOM) {
      this.#showHint();
      return;
    }
    const base = image.getBoundingClientRect();
    this.#zoom = { image, scale, base, x: originX, y: originY, sizes: image.sizes };
    // A larger sizes value makes the browser pick a sharper srcset candidate.
    // It also changes the intrinsic size of a w-descriptor image, so the box
    // is pinned first — otherwise the image would resize under the transform.
    image.style.inlineSize = `${base.width}px`;
    image.style.blockSize = `${base.height}px`;
    image.sizes = `${Math.ceil(base.width * scale)}px`;
    image.style.transformOrigin = `${originX}% ${originY}%`;
    image.style.transform = `scale(${scale})`;
    image.classList.add('hy-product-lightbox__image--zoomed');
    this.#track.classList.add('hy-product-lightbox__track--locked');
    this.#updateZoomButton();
  }

  #unzoom() {
    if (!this.#zoom) return;
    const { image, sizes } = this.#zoom;
    image.style.removeProperty('transform');
    image.style.removeProperty('transform-origin');
    image.style.removeProperty('inline-size');
    image.style.removeProperty('block-size');
    image.sizes = sizes;
    image.classList.remove('hy-product-lightbox__image--zoomed');
    this.#track.classList.remove('hy-product-lightbox__track--locked');
    this.#zoom = null;
    this.#drag = null;
    this.#updateZoomButton();
  }

  #moveOrigin(x, y) {
    this.#zoom.x = clampPercent(x);
    this.#zoom.y = clampPercent(y);
    this.#zoom.image.style.transformOrigin = `${this.#zoom.x}% ${this.#zoom.y}%`;
  }

  // Pointer position as a percentage of the unscaled image box.
  #originFromPoint(image, clientX, clientY) {
    const box = this.#zoom?.base || image.getBoundingClientRect();
    return [
      clampPercent(((clientX - box.left) / box.width) * 100),
      clampPercent(((clientY - box.top) / box.height) * 100),
    ];
  }

  #showHint() {
    const hint = this.querySelector('.hy-product-lightbox__hint');
    if (!hint) return;
    hint.classList.add('hy-product-lightbox__hint--visible');
    clearTimeout(this.#hintTimer);
    this.#hintTimer = setTimeout(() => hint.classList.remove('hy-product-lightbox__hint--visible'), HINT_MS);
  }

  #handleImageClick = (event) => {
    if (this.#lastPointerType === 'touch') return;
    const image = event.target.closest('.hy-product-lightbox__image');
    if (!image || image !== this.#currentImage()) return;
    if (this.#zoom) {
      this.#unzoom();
      return;
    }
    const [x, y] = this.#originFromPoint(image, event.clientX, event.clientY);
    this.#zoomIn(x, y);
  };

  #handlePointerDown = (event) => {
    this.#lastPointerType = event.pointerType;
    if (event.pointerType !== 'touch') return;
    const image = event.target.closest('.hy-product-lightbox__image');
    if (!image || image !== this.#currentImage()) return;

    const tap = { time: event.timeStamp, x: event.clientX, y: event.clientY };
    const previous = this.#lastTap;
    const isDoubleTap = previous
      && tap.time - previous.time < DOUBLE_TAP_MS
      && Math.hypot(tap.x - previous.x, tap.y - previous.y) < DOUBLE_TAP_DISTANCE;

    if (isDoubleTap) {
      this.#lastTap = null;
      if (this.#zoom) this.#unzoom();
      else this.#zoomIn(...this.#originFromPoint(image, tap.x, tap.y));
      return;
    }
    this.#lastTap = tap;
    if (this.#zoom) {
      this.#drag = { x: tap.x, y: tap.y, originX: this.#zoom.x, originY: this.#zoom.y };
      image.setPointerCapture(event.pointerId);
    }
  };

  #handlePointerMove = (event) => {
    if (!this.#zoom) return;
    if (event.pointerType === 'touch') {
      if (!this.#drag) return;
      // Dragging by d moves the image by d when the origin moves by -d / (scale - 1).
      const { base, scale } = this.#zoom;
      const factor = 100 / Math.max(scale - 1, 0.1);
      this.#moveOrigin(
        this.#drag.originX - ((event.clientX - this.#drag.x) / base.width) * factor,
        this.#drag.originY - ((event.clientY - this.#drag.y) / base.height) * factor,
      );
      return;
    }
    const [x, y] = this.#originFromPoint(this.#zoom.image, event.clientX, event.clientY);
    this.#moveOrigin(x, y);
  };

  #handlePointerUp = () => {
    this.#drag = null;
  };

  #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

customElements.define('hy-product-lightbox', HyProductLightbox);
